import { useEffect, useRef, useState } from 'react';
import { Empty, Spin } from 'antd';
import client from '../../api/client';
import endpoints from '../../api/endpoints';

// Stage R1: HTML viewer. Mirrors the invoice viewer's iframe + srcDoc
// pattern (Stage 5N-9A) so the print path stays identical:
//   * sandbox="allow-same-origin allow-modals" (NO allow-scripts).
//   * srcDoc = HTML returned by GET /api/reports/jobs/:id/result.
//   * Browser's Print dialog uses the embedded @page CSS to land on A4.
//
// Zoom / fit-width are surfaced as a CSS transform on the iframe so we
// don't have to re-fetch on every nudge.

const FIT_WIDTH_TARGET = 0.92; // 92% of available container width.

export default function ReportViewer({ jobId, zoom = 1, refreshKey = 0 }) {
  const [html, setHtml]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const containerRef = useRef(null);
  const iframeRef    = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setHtml(null);

    // Fetch as plain text so we can drop it straight into srcDoc.
    client
      .get(endpoints.reports.resultUrl(jobId), { responseType: 'text' })
      .then((resp) => {
        if (cancelled) return;
        const body = typeof resp.data === 'string' ? resp.data : '';
        setHtml(body);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [jobId, refreshKey]);

  if (loading) {
    return (
      <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin tip="Loading report…" />
      </div>
    );
  }
  if (error || !html) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="Could not load this report. Try refreshing in a moment." />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'auto', background: '#f0f2f5' }}>
      <iframe
        ref={iframeRef}
        title="Glass-i Report"
        srcDoc={html}
        sandbox="allow-same-origin allow-modals"
        style={{
          width:           `${100 / zoom}%`,
          height:          `${800 / zoom}px`,
          border:          'none',
          transform:       `scale(${zoom})`,
          transformOrigin: 'top left',
          background:      '#f0f2f5'
        }}
      />
    </div>
  );
}

// Imperative print helper exposed to ReportViewerPage. Calls the
// iframe's contentWindow.print() so the browser dialog uses the
// embedded @page rules instead of the outer page's.
export function printIframe(iframe) {
  if (!iframe || !iframe.contentWindow) return false;
  try {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    return true;
  } catch (_e) {
    return false;
  }
}

export { FIT_WIDTH_TARGET };
